// src/components/tasks/FamilyProgressCard.tsx
// Shows task progress for all family members in a compact card layout
import React, { useMemo, useState, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { star, checkmarkCircle, chevronDown, chevronUp } from 'ionicons/icons';
import { Card } from '../ui/Card';
import { TaskItem } from './TaskItem';
import type { TaskAssignment, FamilyMember } from '../../types';
import './tasks.css';

export interface FamilyProgressCardProps {
  assignments: TaskAssignment[];
  familyMembers: FamilyMember[];
  onMemberClick?: (member: FamilyMember) => void;
  onTaskClick?: (assignment: TaskAssignment) => void;
  onTaskToggle?: (assignmentId: string, completed: boolean, event: React.MouseEvent) => void;
  showUnassigned?: boolean;
  expandable?: boolean;
}

interface MemberProgress {
  member: FamilyMember | null;
  memberId: string;
  total: number;
  completed: number;
  points: number;
  isComplete: boolean;
}

export const FamilyProgressCard: React.FC<FamilyProgressCardProps> = ({
  assignments,
  familyMembers,
  onMemberClick,
  onTaskClick,
  onTaskToggle,
  showUnassigned = false,
  expandable = false,
}) => {
  // Track which member is expanded
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  // Get tasks for expanded member
  const expandedMemberTasks = useMemo(() => {
    if (!expandedMemberId) return [];
    return assignments
      .filter(a => a.assigned_to === expandedMemberId)
      .sort((a, b) => {
        // Incomplete first, then by due date
        if (a.is_completed !== b.is_completed) return a.is_completed ? 1 : -1;
        return (a.due_date || '').localeCompare(b.due_date || '');
      });
  }, [assignments, expandedMemberId]);

  // Handle member card click
  const handleMemberClick = useCallback((member: FamilyMember | null, memberId: string) => {
    if (expandable && member) {
      // Toggle expansion
      setExpandedMemberId(prev => prev === memberId ? null : memberId);
    } else if (member && onMemberClick) {
      onMemberClick(member);
    }
  }, [expandable, onMemberClick]);
  // Calculate progress for each member
  const memberProgress = useMemo((): MemberProgress[] => {
    const progressMap: Record<string, MemberProgress> = {};

    // Initialize with all family members
    familyMembers.forEach(member => {
      progressMap[member.id] = {
        member,
        memberId: member.id,
        total: 0,
        completed: 0,
        points: 0,
        isComplete: false,
      };
    });

    // Track unassigned if requested
    if (showUnassigned) {
      progressMap['unassigned'] = {
        member: null,
        memberId: 'unassigned',
        total: 0,
        completed: 0,
        points: 0,
        isComplete: false,
      };
    }

    // Aggregate assignments
    assignments.forEach(assignment => {
      const memberId = assignment.assigned_to || 'unassigned';
      if (!progressMap[memberId] && !showUnassigned) return;

      if (!progressMap[memberId]) {
        progressMap[memberId] = {
          member: null,
          memberId,
          total: 0,
          completed: 0,
          points: 0,
          isComplete: false,
        };
      }

      progressMap[memberId].total++;
      if (assignment.is_completed) {
        progressMap[memberId].completed++;
        progressMap[memberId].points += assignment.chore?.points_value || 0;
      }
    });

    // Calculate completion status and filter out empty
    return Object.values(progressMap)
      .filter(p => p.total > 0 || p.member)
      .map(p => ({
        ...p,
        isComplete: p.total > 0 && p.completed === p.total,
      }))
      .sort((a, b) => {
        // Sort: members with tasks first, then by completion percentage
        if (a.total === 0 && b.total > 0) return 1;
        if (b.total === 0 && a.total > 0) return -1;
        const aPercent = a.total > 0 ? a.completed / a.total : 0;
        const bPercent = b.total > 0 ? b.completed / b.total : 0;
        return bPercent - aPercent;
      });
  }, [assignments, familyMembers, showUnassigned]);

  // Calculate overall stats
  const overallStats = useMemo(() => {
    const withTasks = memberProgress.filter(p => p.total > 0);
    const allComplete = withTasks.length > 0 && withTasks.every(p => p.isComplete);
    const totalPoints = memberProgress.reduce((sum, p) => sum + p.points, 0);
    return { allComplete, totalPoints, activeMembers: withTasks.length };
  }, [memberProgress]);

  return (
    <Card
      title="Family Progress"
      subtitle={overallStats.allComplete ? 'All done! 🎉' : `${overallStats.activeMembers} with tasks`}
      size="lg"
    >
      <div className="family-progress-card">
        {memberProgress.length === 0 ? (
          <div className="family-progress-card__empty">
            <span className="family-progress-card__empty-icon">👨‍👩‍👧‍👦</span>
            <p>No family members yet</p>
          </div>
        ) : (
          <div className="family-progress-card__grid">
            {memberProgress.map(({ member, memberId, total, completed, points, isComplete }) => {
              const isUnassigned = memberId === 'unassigned';
              const memberColor = member?.color || 'var(--ion-color-medium)';
              const percentage = total > 0 ? (completed / total) * 100 : 0;
              const isExpanded = expandedMemberId === memberId;

              return (
                <div key={memberId} className="family-progress-card__member-container">
                  <button
                    className={`family-progress-card__member ${isComplete ? 'complete' : ''} ${total === 0 ? 'no-tasks' : ''} ${isExpanded ? 'expanded' : ''}`}
                    style={{ '--member-color': memberColor } as React.CSSProperties}
                    onClick={() => handleMemberClick(member, memberId)}
                    disabled={!member}
                  >
                    {/* Avatar */}
                    <div className="family-progress-card__avatar">
                      {isUnassigned ? (
                        <span className="family-progress-card__avatar-placeholder">?</span>
                      ) : member?.avatar_url ? (
                        <img src={member.avatar_url} alt="" />
                      ) : (
                        <span className="family-progress-card__avatar-emoji">
                          {member?.avatar_emoji || '👤'}
                        </span>
                      )}
                      {isComplete && (
                        <div className="family-progress-card__complete-badge">
                          <IonIcon icon={checkmarkCircle} />
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <span className="family-progress-card__name">
                      {isUnassigned ? 'Unassigned' : member?.nickname || member?.name || 'Unknown'}
                    </span>

                    {/* Progress bar */}
                    {total > 0 && (
                      <div className="family-progress-card__progress">
                        <div
                          className="family-progress-card__progress-fill"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    )}

                    {/* Stats */}
                    <div className="family-progress-card__stats">
                      {total > 0 ? (
                        <>
                          <span className="family-progress-card__count">
                            {completed}/{total}
                          </span>
                          {points > 0 && (
                            <span className="family-progress-card__points">
                              <IonIcon icon={star} />
                              {points}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="family-progress-card__no-tasks">No tasks</span>
                      )}
                    </div>

                    {/* Expand indicator */}
                    {expandable && total > 0 && (
                      <IonIcon
                        icon={isExpanded ? chevronUp : chevronDown}
                        className="family-progress-card__expand-icon"
                      />
                    )}
                  </button>

                  {/* Expanded task list */}
                  {isExpanded && expandedMemberTasks.length > 0 && (
                    <div className="family-progress-card__tasks">
                      {expandedMemberTasks.map(assignment => (
                        <TaskItem
                          key={assignment.id}
                          assignment={assignment}
                          onToggle={onTaskToggle || (() => {})}
                          onClick={() => onTaskClick?.(assignment)}
                          showDueDate
                          showPoints
                          compact
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

FamilyProgressCard.displayName = 'FamilyProgressCard';

export default FamilyProgressCard;
